/** Keep this function self-contained: the prerenderer embeds its source before
 * any module loads, so recovery still works when the main script never arrives.
 * It only changes the disposable app shell, never a public page or React UI. */
export function installStartupRecovery(): void {
  const html = document.documentElement;
  if (html.getAttribute("data-surface") !== "canvas" || html.hasAttribute("data-crimp-startup-watch")) return;
  html.setAttribute("data-crimp-startup-watch", "pending");
  let finished = false;
  const timer = window.setTimeout(() => show(false), 15_000);

  function complete(): void {
    finished = true;
    window.clearTimeout(timer);
    window.removeEventListener("crimp:startup-complete", complete);
    window.removeEventListener("crimp:startup-failed", failed);
    window.removeEventListener("error", scriptFailed, true);
    html.setAttribute("data-crimp-startup-watch", "complete");
  }

  function show(error: boolean): void {
    if (finished) return;
    const shell = document.getElementById("app-static");
    if (!shell && document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => show(error), { once: true });
      return;
    }
    // React removes this marker when it commits. Never replace a mounted app.
    if (!shell) return;
    shell.textContent = "";
    shell.setAttribute("role", "alert");
    shell.setAttribute("data-startup-recovery", "");
    shell.style.cssText = "position:fixed;inset:0;z-index:10000;display:grid;place-items:center;overflow:auto;padding:24px;opacity:1;animation:none;background:var(--color-bg,#faf9f6);color:var(--color-fg,#171717);font-family:var(--font-sans,system-ui,sans-serif)";
    const style = document.createElement("style");
    style.textContent = "#app-static[data-startup-recovery]::before{display:none}";
    const panel = document.createElement("div");
    panel.style.cssText = "max-width:360px;text-align:center";
    const heading = document.createElement("h1");
    heading.textContent = error ? "Crimp couldn’t finish loading" : "Crimp is taking longer to load";
    heading.style.cssText = "margin:0 0 12px;font-size:20px;font-weight:600;line-height:1.3";
    const description = document.createElement("p");
    description.textContent = "Check your connection and try again. Retrying won’t clear your saved projects.";
    description.style.cssText = "margin:0 0 20px;font-size:15px;line-height:1.5";
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = "Try again";
    retry.style.cssText = "min-height:44px;padding:10px 20px;border:1px solid currentColor;border-radius:8px;background:var(--color-surface,#fff);color:inherit;font:inherit;font-weight:600;cursor:pointer";
    retry.addEventListener("click", () => {
      retry.disabled = true;
      // Reload the same URL, including its starter query and hash. No storage
      // is cleared and only the user can request another attempt here.
      window.location.reload();
    });
    panel.append(heading, description, retry);
    shell.append(style, panel);
  }

  function failed(): void {
    window.clearTimeout(timer);
    show(true);
  }

  function scriptFailed(event: Event): void {
    const target = event.target as HTMLScriptElement | null;
    if (target?.tagName === "SCRIPT" && target.type === "module") failed();
  }

  window.addEventListener("crimp:startup-complete", complete);
  window.addEventListener("crimp:startup-failed", failed);
  window.addEventListener("error", scriptFailed, true);
}

export function startupRecoveryScript(): string {
  return `(${installStartupRecovery.toString()})();`;
}

export function completeStartupRecovery(): void {
  window.dispatchEvent(new Event("crimp:startup-complete"));
}

export function failStartupRecovery(): void {
  window.dispatchEvent(new Event("crimp:startup-failed"));
}

/** Optional startup metadata must not hold the route hostage to a stalled
 * request. Late success/rejection is handled without replaying entry events. */
export async function optionalStartupModule<T>(request: Promise<T>, waitMs: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request.catch(() => null),
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), waitMs); }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}
